import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { isPushSupported, subscribeToPush, isSubscribed, registerServiceWorker } from "../lib/pushManager";
import { Bell, X } from "lucide-react";

const DISMISS_KEY = "ameenji_push_prompt_dismissed";

export function PushNotificationPrompt() {
  const { api, user } = useAuth();
  const [show, setShow] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const attemptAutoSubscribe = useCallback(async () => {
    if (!isPushSupported() || !user || !api) return;

    await registerServiceWorker();

    // If already subscribed, re-register to ensure backend has latest
    if (isSubscribed() && Notification.permission === "granted") {
      try { await subscribeToPush(api); } catch {}
      return;
    }

    // If permission already granted but not subscribed, auto-subscribe
    if (Notification.permission === "granted") {
      await subscribeToPush(api);
      return;
    }

    // Show prompt if not dismissed
    const dismissed = sessionStorage.getItem(DISMISS_KEY);
    if (!dismissed && Notification.permission === "default") {
      setTimeout(() => setShow(true), 3000);
    }
  }, [api, user]);

  useEffect(() => {
    attemptAutoSubscribe();
  }, [attemptAutoSubscribe]);

  const handleEnable = async () => {
    setSubscribing(true);
    await subscribeToPush(api);
    setSubscribing(false);
    setShow(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 duration-500" data-testid="push-notification-prompt">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#155155]/10 flex items-center justify-center flex-shrink-0">
            <Bell size={18} className="text-[#155155]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">Enable Notifications</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Get notified about new leads, messages, tasks & leave updates - even when the app is closed.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleEnable}
                disabled={subscribing}
                className="px-3 py-1.5 bg-[#155155] text-white text-xs rounded-lg font-medium hover:bg-[#0E4040] transition-colors disabled:opacity-50"
                data-testid="enable-push-btn"
              >
                {subscribing ? "Enabling..." : "Enable"}
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="dismiss-push-btn"
              >
                Not now
              </button>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
